import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { PermissionCheckPipe } from '@app/pipes/permission-flag.pipe';
import { getAuthenticatedStatus } from '@app/reducers/auth/auth-service.selectors';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { SubscriptionPlanDirective } from '@app/shared/subscription-plan/subscription-plan.directive';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { mgcIconExitRegular } from '@sb/mgc-icons/mgcIcon-exit_regular.icon';
import { mgcIconFlaskRegular } from '@sb/mgc-icons/mgcIcon-flask_regular.icon';
import { mgcIconInboxRegular } from '@sb/mgc-icons/mgcIcon-inbox_regular.icon';
import { mgcIconPaletteRegular } from '@sb/mgc-icons/mgcIcon-palette_regular.icon';
import { mgcIconProfileRegular } from '@sb/mgc-icons/mgcIcon-profile_regular.icon';
import { mgcIconSendPlaneRegular } from '@sb/mgc-icons/mgcIcon-send_plane_regular.icon';
import { mgcIconSparkles2Regular } from '@sb/mgc-icons/mgcIcon-sparkles_2_regular.icon';
import { mgcIconStopwatchRegular } from '@sb/mgc-icons/mgcIcon-stopwatch_regular.icon';
import { mgcIconTransfer3Regular } from '@sb/mgc-icons/mgcIcon-transfer_3_regular.icon';
import { featureFlag, FeatureFlagPipe } from '@sb/shared/feature';
import { hasPermission } from '@sb/shared/permission/util';
import { AvatarComponent } from '@sb/ui/avatar';
import { NotificationBadgeComponent } from '@sb/ui/badge';
import { SbDialogService } from '@sb/ui/dialog';
import { IconRegistry } from '@sb/ui/icon';
import {
  MenuComponent,
  MenuItemComponent,
  MenuItemRadioComponent,
  MenuSectionComponent,
  MenuSeparatorComponent,
  MenuTriggerDirective,
} from '@sb/ui/menu';
import { SidebarFooterComponent } from '@sb/ui/navigation';
import { PlanType } from '@shiftbase-com/ts-shared/models';

import { getAuthenticatedUser } from '../../../reducers/orm/employee/employee.selectors';
import { LabsDialogComponent } from '../../labs-dialog/labs-dialog.component';
import { SBHeaderService } from '../sb-header/sb-header.service';
import { RequestsBadgeService } from '../sidebar/requests/requests-badge.service';
import { useThemeChoice } from '../theme-choice';

/** Statuses that let a user in with nothing but their account settings and a way out. */
const SOFT_LOGIN_STATUSES = ['paused', 'expired', 'paymentRequired'];

const CLOCK_MODAL_LINK = ['', { outlets: { modal: ['clock'] } }];
const CLOCK_SWITCH_MODAL_LINK = ['', { outlets: { modal: ['clock-switch'] } }];

/**
 * The account menu in the side navigation's footer — its trigger and its panel together, because
 * `[sbMenuTriggerFor]` needs the panel instance and a parent template cannot read one out of a
 * child that has not been created yet.
 */
@Component({
  selector: 'app-side-nav-profile',
  imports: [
    AvatarComponent,
    FeatureFlagPipe,
    MenuComponent,
    MenuItemComponent,
    MenuItemRadioComponent,
    MenuSectionComponent,
    MenuSeparatorComponent,
    MenuTriggerDirective,
    NotificationBadgeComponent,
    PermissionCheckPipe,
    RouterLink,
    SidebarFooterComponent,
    SubscriptionPlanDirective,
    TranslatePipe,
  ],
  templateUrl: './app-side-nav-profile.component.html',
  host: {
    // The sidebar projects on the `sb-sidebar-footer` element name and lays its footer out inside
    // its own strip, so this wrapper has to leave no box of its own behind.
    class: 'contents',
  },
})
export class AppSideNavProfileComponent {
  #store = inject<Store<AppState>>(Store);
  #dialogService = inject(SbDialogService);

  headerService = inject(SBHeaderService);

  planType = PlanType;
  Features = featureFlag;

  clockModalLink = CLOCK_MODAL_LINK;
  clockSwitchModalLink = CLOCK_SWITCH_MODAL_LINK;

  theme = useThemeChoice();

  #authenticatedStatus = this.#store.selectSignal(getAuthenticatedStatus);
  isSoftLogin = computed(() => {
    const status = this.#authenticatedStatus();

    return !!status && SOFT_LOGIN_STATUSES.includes(status);
  });

  user = this.#store.selectSignal(getAuthenticatedUser);

  #requestsBadge = inject(RequestsBadgeService);

  supervisorOpenRequests = this.#requestsBadge.supervisorCount;
  myOpenRequests = this.#requestsBadge.myCount;

  #permissionState = this.#store.selectSignal(getPermissionState);
  #hasSupervisorPermission = computed(() =>
    hasPermission(
      { permissions: ['Approve absentee', 'Approve exchange'], userId: 'me', departments: 'any' },
      this.#permissionState(),
    ),
  );

  // The badge on the closed menu stands for whatever is waiting inside it, so it counts only the
  // request rows this user can actually see.
  notificationCount = computed(
    () => this.myOpenRequests() + (this.#hasSupervisorPermission() ? this.supervisorOpenRequests() : 0),
  );

  // One subscription for both clock rows: `clockState$` re-runs its own HTTP load per subscriber.
  clockState = toSignal(this.headerService.clockState$);
  showSwitchButton = toSignal(this.headerService.showSwitchButton$);

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconExitRegular,
      mgcIconFlaskRegular,
      mgcIconInboxRegular,
      mgcIconPaletteRegular,
      mgcIconProfileRegular,
      mgcIconSendPlaneRegular,
      mgcIconSparkles2Regular,
      mgcIconStopwatchRegular,
      mgcIconTransfer3Regular,
    ]);
  }

  openLabsDialog(): void {
    this.#dialogService.open(LabsDialogComponent, {});
  }
}
